import { io, Socket } from 'socket.io-client';
import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { usePkce } from '@oliasoft/pkce';

import { MessageType, toast } from '@oliasoft-open-source/react-ui-library';
import { DesignSyncStatusType, NotificationEventStatusType, NotificationEventType, WebsocketEvents } from 'common/enums/notification-service-enums';
import { INotificationMetadata } from 'common/interfaces/notification';
import { NotificationScopesEnum } from '@oliasoft/notification-service-client';
import { RootState } from '../configureStore';
import { setAfterUnpackedActiveArchiveState } from '../ui/hierarchy-tree/hierarchy-tree';
import { progressUpdated } from '../entities/upload/upload';
import { syncStatusReceived } from '../entities/hierarchy/design/design-sync-status';
import { getDesign } from '../entities/hierarchy/design/design';

type SocketContextProps = {
  isConnected: boolean;
  socket: Socket;
};

type SocketProviderProps = PropsFromRedux & {
  children: ReactElement;
};

export const SocketContext = createContext<SocketContextProps | null>(null);

const SocketProvider = ({
  children,
  selectedCustomer,
  selectedArchiveId,
}: SocketProviderProps) => {
  const { accessToken } = usePkce();
  const dispatch = useDispatch();
  const hostEnv = window.__runtimeConfigStatic?.host_env;
  const wsBaseUrl = window.__runtimeConfigStatic?.notification_service.websocket_url;

  const [isConnected, setConnected] = useState(false);

  const socket = useMemo(() => {
    console.log('Token renewed. Creating a new socket instance.');

    const newSocket = io(`${wsBaseUrl}`, {
      addTrailingSlash: false,
      path: '/websockets',
      autoConnect: !!accessToken, // Only auto-connect if we have a token
      withCredentials: true,
      query: {
        hostenv: hostEnv,
      },
      auth: {
        token: accessToken, // Use the latest token in the handshake
      },
    });

    return newSocket;
  }, [accessToken]);

  const companyId = selectedCustomer?.companyId;

  const handleDesignSyncMessage = (notification: { metadata: INotificationMetadata; message: string }) => {
    const { designid, isSyncing, status } = notification.metadata as any;
    if (designid) {
      dispatch(syncStatusReceived({ designid, isSyncing: !!isSyncing }));
      if (status === DesignSyncStatusType.Synched) {
        dispatch(getDesign(designid, ''));
      }
    }

    const results: Record<string, MessageType> = {
      [DesignSyncStatusType.Synched]: MessageType.SUCCESS,
      [DesignSyncStatusType.Error]: MessageType.ERROR,
    };
    if (results[status]) {
      toast({
        message: {
          type: results[status],
          icon: true,
          content: notification.message
        },
      });
    }
  };

  useEffect(() => {
    if (!socket.connected || !companyId) return;
    socket.emit(WebsocketEvents.ChangeScope, { scope: NotificationScopesEnum.group, value: companyId }); // register inside group room
    console.log('Changed group scope', companyId, socket.id);
  }, [companyId, isConnected]);

  useEffect(() => {
    function handleNotificationMessage(msg: { notification: { message: string; metadata: INotificationMetadata, progress: number, status: string } }) {
      const { notification } = msg;
      if (notification.metadata?.eventType === NotificationEventType.Unpack) {
        switch (notification.metadata.eventStatus) {
          case NotificationEventStatusType.Success: {
            toast({
              message: {
                type: MessageType.SUCCESS,
                icon: true,
                content: notification.message,
              },
              id: 'toast-unpacking-success',
            });
            const { archiveid } = notification.metadata;
            if (selectedArchiveId === archiveid) {
              dispatch(setAfterUnpackedActiveArchiveState(true));
            }
            break;
          }
          case NotificationEventStatusType.Error: {
            toast({
              message: {
                type: MessageType.ERROR,
                icon: true,
                content: notification.message,
              },
              id: 'toast-unpacking-failed',
            });
            break;
          }
        }
      }
      if (notification.metadata?.eventType === NotificationEventType.Progress) {
        dispatch(progressUpdated({
          status: notification.status,
          progress: notification.progress,
        }));
      }

      if (notification.metadata?.eventType === NotificationEventType.DesignSync) {
        handleDesignSyncMessage(notification);
      }
    }

    socket.on(WebsocketEvents.NotificationMessage, handleNotificationMessage);
    return () => {
      socket.off(WebsocketEvents.NotificationMessage, handleNotificationMessage);
    };
  }, [socket, selectedArchiveId]);

  useEffect(() => {
    if (!accessToken) {
      console.log('No access token. Skipping socket setup.');
      setConnected(false);
      return;
    }

    function onConnect() {
      console.log('Authenticated and Connected!');
      setConnected(true);
    }

    function onDisconnect(reason: Socket.DisconnectReason) {
      console.log('Disconnected with reason', reason);
      setConnected(false);
    }

    function handleConnectError(err: Error) {
      console.error('Connection has not been established.', err.message);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', handleConnectError);

    // If a new socket was created (due to token change), ensure it connects.
    // If it was already connected, it will be disconnected by the `return` cleanup below.
    if (!socket.connected) {
      socket.connect();
    }

    // This runs when the component unmounts OR when the `socket` dependency changes (i.e., token refresh).
    return () => {
      console.log('Cleaning up old socket instance and listeners.');
      // Remove listeners from the old instance
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', handleConnectError);

      // Manually close the old socket connection when a new one is created
      // or when the component unmounts to prevent background connections.
      socket.close();
      setConnected(false);
    };
  }, [socket, accessToken]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
};

const mapStateToProps = ({ ui, entities }: RootState) => ({
  selectedArchiveId: ui?.hierarchyTree.selectedArchiveId,
  selectedCustomer: entities?.customer.selectedCustomer,
});

const mapDispatchToProps = {
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = connector(SocketProvider);

export { Container as SocketProvider };

export const useSocket = () => useContext<any>(SocketContext);
